/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../logger';

const logger = Logger.getInstance();

// Query parameters used by click servers to carry the next URL in a clickthrough
// chain. Observed live: doubleclick uses `adurl`, 6sense uses `redirect`.
const CLICKTHROUGH_REDIRECT_PARAMS = ['adurl', 'redirect', 'url', 'r'];

// Chains observed live are 2-3 hops (doubleclick -> advertiser, or
// doubleclick -> click tracker -> advertiser). This only bounds a malformed or
// self-referential chain.
const MAX_CLICKTHROUGH_HOPS = 6;

// Ad-serving and click-tracking hosts that legitimately appear inside a click
// chain but are never the buyer. A walk that ENDS on one of these has hit a
// tracker whose destination is resolved server-side — CM360's /ddm/trackclk
// carries no client-visible destination at all — so there is nothing to
// attribute and the walk yields nothing rather than record ad infrastructure
// as an advertiser. Before this guard, ad.doubleclick.net was 8.5% of
// programmatic video captures (US-skewed demand; measured 2026-07-31), with
// d.agkn.com the next offender. Matched as the domain itself or any
// subdomain of it.
const NON_ADVERTISER_TERMINAL_DOMAINS = [
  'doubleclick.net',
  'doubleclickbygoogle.com',
  'googleadservices.com',
  'googlesyndication.com',
  'agkn.com',
  'adform.net',
  'adnxs.com',
  '6sc.co',
  'amazon-adsystem.com',
  'flashtalking.com',
  'serving-sys.com',
  'innovid.com',
];

/** True when the hostname is a known ad-infrastructure domain or a subdomain of one. */
const isNonAdvertiserDomain = (domain: string): boolean =>
  NON_ADVERTISER_TERMINAL_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`));

/** Hostname of a URL string, or an empty string when it cannot be parsed. */
const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch (error) {
    return '';
  }
};

interface ClickThroughDomain {
  domain: string;
  chain: string[];
}

/**
 * Recovers the advertiser domain of an exchange-bought ad from the click chain
 * in its IMA trafficking parameters.
 *
 * Open-auction ads carry no advertiser name and no bid, so the clickthrough is
 * the only client-side signal identifying the buyer. Measured live across
 * TechRadar, Tom's Guide and Cinemablend, the structure is consistent:
 * `clickThroughUrl` is a single string whose hops nest inside redirect query
 * parameters (encoding compounds per hop), and the **final hop is the
 * advertiser**:
 *
 *   adclick.g.doubleclick.net -> www.hotpoint.co.uk
 *   servedby.flashtalking.com -> www.marksandspencer.com
 *   adclick.g.doubleclick.net -> t.6sc.co -> www.dell.com
 *
 * Only `clickThroughUrl` is read. The other keys (`mediaFiles`, `plugins`,
 * `trackingUri`, `adParameters`) hold measurement and CDN hosts that look
 * structurally identical to an advertiser domain, so scanning the whole object
 * would pick those up instead.
 *
 * Two clean failure modes, both yielding nothing rather than something wrong:
 * a DSP buying direct through the exchange returns empty trafficking
 * parameters, and some verification wrappers leave `clickThroughUrl` empty.
 *
 * Takes the raw parameter string rather than a `google.ima.Ad` so it stays a
 * pure function shared by every IMA-backed player (JW Player, Digiteka).
 *
 * The full chain is returned alongside the domain so it can be logged: if the
 * walk stops early on an unrecognised redirect parameter it will surface a
 * click tracker as the advertiser, and the chain is what makes that visible.
 */
const extractClickThroughDomain = (
  traffickingParametersString: string | null | undefined,
): ClickThroughDomain | null => {
  if (!traffickingParametersString) {
    return null;
  }

  let clickThroughUrl: unknown;
  try {
    clickThroughUrl = JSON.parse(traffickingParametersString)?.clickThroughUrl;
  } catch (error) {
    return null;
  }
  if (typeof clickThroughUrl !== 'string' || !clickThroughUrl) {
    return null;
  }

  const chain: string[] = [];
  let current: string | null = clickThroughUrl;
  for (let hop = 0; hop < MAX_CLICKTHROUGH_HOPS && current; hop++) {
    let url: URL;
    try {
      url = new URL(current);
    } catch (error) {
      break;
    }
    chain.push(url.hostname);

    let next: string | null = null;
    for (const param of CLICKTHROUGH_REDIRECT_PARAMS) {
      const value = url.searchParams.get(param);
      if (value && /^https?:\/\//.test(value)) {
        next = value;
        break;
      }
    }
    if (!next || chain.includes(hostOf(next))) {
      break;
    }
    current = next;
  }

  if (chain.length < 2) {
    // A single hop is the click server itself, not an advertiser.
    return null;
  }

  // Normalised to the bare-domain shape Prebid's meta.advertiserDomains uses,
  // so the same advertiser does not categorise twice as "x.com" and "www.x.com".
  const domain = chain[chain.length - 1].replace(/^www\./, '');
  if (isNonAdvertiserDomain(domain)) {
    logger.debug(
      `Clickthrough chain ended on ad infrastructure, no advertiser [${chain.join(' -> ')}]`,
    );
    return null;
  }
  logger.debug(`Clickthrough chain resolved to ${domain} [${chain.join(' -> ')}]`);
  return { domain, chain };
};

export { extractClickThroughDomain, type ClickThroughDomain };
